import { cloneDeep, isUndefined } from 'lodash';
import queryString from 'query-string';
import { v4 as uuid4 } from 'uuid';
import { Message } from '@meitu/saas-ui';
import { hexToRgba, rgbaToHexa } from '@meitu/saas-colors';
import { createTextLayer, loadTextMarkSVG } from '@meitu/saas-editor-core/es/layers/text-layer/utils';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { fetchSvgContentWithCache, loadImageAsync, readFileMd5 } from '@meitu/saas-editor-core/es/utils/file-kit';
import { traverseTree, traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { SizeUnitType } from '@meitu/saas-editor-core/es/utils/unit-kit';
import { uploadFileToObs } from '@meitu/saas-editor-core/es/utils/upload-kit/obs-manager';
import { getDefaultClientBackgroundValue, LATEST_VERSION } from '@meitu/saas-editor-core/es/version-parse';
import {
    CanvasType,
    IAdvancedTextMaterial,
    IBackgroundLayerProps,
    IExtraInfo,
    IImageLayerProps,
    ILayerProps,
    ITextLayerProps,
    LayerType,
    StageType,
} from '@meitu/saas-editor-core/es/version-types';
import { CollapseType } from '@meitu/saas-editor-ui';
import { IParsePsdToClientTemplateResult, IStage, PsdParseModal } from '@meitu/x-design-file-parser';
import { useAddVectorLayer } from '@/editor/collapse/views/material-collapse/hooks/add-vector';
import useFetchAndApplyPPTDemo from '@/editor/components/apply-ppt-template/use-fetch-and-apply-ppt-demo';
import { TextMarkSvgUpload } from '@/editor/components/text-mark-svg-upload';
import {
    TextArtDefaultFontFamily,
    TextArtDefaultStage,
    TextArtDefaultText,
} from '@/editor/panel/views/text-art-panel/utils/text-art-opera';
import {
    setTextMarkSvg,
    TextMarkDefaultFontFamily,
    TextMarkDefaultStage,
    TextMarkDefaultText,
} from '@/editor/panel/views/text-mark-panel/utils/text-mark-opera';
import { getLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { fetchAIPPTTemplateComponent, fetchAIPPTTemplateDetail } from '@/services/ai';
import { fetchSvgMaterialDetail } from '@/services/material';
import { getMaterialLayerInfoById } from '@/services/tag';
import {
    fetchAITemplate,
    fetchTemplateDetail,
    fetchTextTemplateDetail,
    getWatermarkTemplate,
} from '@/services/template';
import { fetchTextArt, fetchTextMark } from '@/services/text';
import { useStoreActions } from '@/store';
import { getQuery } from '@/utils/query';
import { useFeatureHelper } from '../use-feature-helper';
import {
    isAITemplate,
    isEditAIPPTComponent,
    isEditAIPPTTemplate,
    isEditMaterial,
    isEditorWatermark,
    isEditTagMaterial,
    isEditTextArt,
    isEditTextMark,
    isPPTDemoAndShowFullTemplate,
    isTextTemplate,
    isUploadAIPPTComponent,
    isUploadAIPPTTemplate,
    isUploadPPT,
    isUploadTemplatePsd,
    isUploadTextMaterialPsd,
} from './editor-mode';
import { useInitEmptyTemplate } from './init-empty-template';
import { useInitEditorStore } from './init-store';
import { transferServerMaterialLayerToClient, transferTemplateToClient } from './server-to-client';
import { adjustFontFamily } from './utils';

export interface ICreateEmptyOption {
    width: number;
    height: number;
    imgUrl?: string;
    unit: SizeUnitType;
    func?: string;
    createInfo?: Record<string, any>;
    isPPT?: boolean;
}

const fetchTemplate = async (id: string, is_new: string) => {
    const tempReq: any = await fetchTemplateDetail({ id, is_new });
    return tempReq;
};

/** 默认设计文件名 */
export const TEMPLATE_NAME = () => {
    const { $t } = getLocaleStore();
    return $t((v) => v.hooks.designFileName);
};

// 合并接口跟配方的 ExtraInfo 字段
const pickLayersAndMergeExtraInfo = (item: any, extraInfo: IExtraInfo, init: boolean) => {
    const layers = item.layers;
    delete item.layers;
    const stageExtraInfo: IExtraInfo = { ...item, ...extraInfo };
    const { editorColor, originId, threshold } = item;
    // console.log('item=>', item, extraInfo);
    if (init) {
        // 初始化，配方里有值，就不读取api的值
        if (editorColor) stageExtraInfo.editorColor = editorColor;
        // if (originId) stageExtraInfo.originId = `${originId}`;
        if (isUndefined(item.threshold)) {
            stageExtraInfo.threshold = extraInfo.threshold;
        } else {
            stageExtraInfo.threshold = threshold;
        }
    }
    // console.log('【stageExtraInfo】：', stageExtraInfo);
    return { stageExtraInfo, layers };
};

// 组装配方数据到编辑器
export const packageTemplateConf = (templateConf: any, extraInfo: IExtraInfo, init: boolean) => {
    const extraInfos: IExtraInfo[] = [];
    const stages: ILayerProps[][] = [];
    templateConf.forEach((element: any) => {
        const { stageExtraInfo, layers } = pickLayersAndMergeExtraInfo(element, extraInfo, init);
        extraInfos.push(stageExtraInfo);
        stages.push(layers);
    });
    return { stages, extraInfos };
};

/**
 * 初始化舞台
 * @returns
 */
export const useInitStage = () => {
    return {};
};

/**
 * 创建空白画布
 * @returns
 */
/**
 * 创建空白画布
 * @returns
 */
export const useCreateEmptyStage = () => {
    const initEditorStore = useInitEditorStore();
    const initEmptyTemplate = useInitEmptyTemplate();
    const setTemplateName = useStoreActions((actions) => actions.templateModel.setTemplateName);

    return {
        async action() {
            const query = getQuery();

            let { stageWidth, stageHeight, open = '', width, height, unit = SizeUnitType.px } = query;
            stageWidth = width || stageWidth || '1242';
            stageHeight = height || stageHeight || '2688';
            initEditorStore.init();

            await initEmptyTemplate(
                parseInt(stageWidth),
                parseInt(stageHeight),
                unit as SizeUnitType,
                open,
                {},
                undefined,
            );
            // 生成封面图

            setTemplateName(TEMPLATE_NAME());
        },
    };
};

/**
 * 获取模板
 */
const useFetchTemplate = () => {
    const { $t } = getLocaleStore();
    const createEmpty = useCreateEmptyStage();
    const setResolution = useStoreActions((actions) => actions.templateModel.setResolution);
    const setPsdFileUrl = useStoreActions((actions) => actions.templateModel.setPsdFileUrl);
    const setCanvasType = useStoreActions((actions) => actions.templateModel.setCanvasType);
    const setCurrentType = useStoreActions((actions) => actions.collapseModel.setCurrentType);
    const featureHelper = useFeatureHelper();
    const setTemplateName = useStoreActions((actions) => actions.templateModel.setTemplateName);
    const initTemplateInfo = useStoreActions((actions) => actions.templateModel.initTemplateInfo);
    const setQrCode = useStoreActions((actions) => actions.templateModel.setQrCode);
    const setExtraInfo = useStoreActions((actions) => actions.templateModel.setExtraInfo);
    const setServerTemplateId = useStoreActions((actions) => actions.templateModel.setServerTemplateId);
    const setMCPSourceId = useStoreActions((actions) => actions.templateModel.setMCPSourceId);
    const setTagInfoId = useStoreActions((actions) => actions.templateModel.setTagInfoId);
    const addVectorLayer = useAddVectorLayer();
    useFetchAndApplyPPTDemo(editor);
    const setTextLayerId = useStoreActions((actions) => actions.textMarkModel.setTextLayerId);
    const setMarkConfig = useStoreActions((actions) => actions.textMarkModel.setMarkConfig);
    const setTextArtConfig = useStoreActions((actions) => actions.textMarkModel.setTextArtConfig);

    const createTemplateByPsd = async (
        data: IParsePsdToClientTemplateResult | null,
        isTextMaterial: boolean,
        isFromPPTUpload: boolean,
    ) => {
        if (!data) {
            return;
        }

        const { stages, resolution, name } = data;
        setResolution(resolution);

        const version = LATEST_VERSION;
        const preview = stages.length > 1 ? '' : stages[0].preview;

        const fontsRequired: string[] = [];

        const templateConf = [] as any;

        stages.forEach((stage: IStage) => {
            const { clientLayers, preview, stageWidth, stageHeight, name } = stage;

            const layers = clientLayers.map((layer: any) => {
                traverseTree(layer, (child: ILayerProps) => {
                    if (child.layerType === LayerType.TEXT_LAYER) {
                        const { textContents, fontFamily } = child.data as ITextLayerProps;

                        fontsRequired.push(fontFamily);

                        if (textContents && textContents.length > 0) {
                            for (let i = 0; i < textContents.length; i++) {
                                fontsRequired.push(textContents[i].fontFamily);
                            }
                        }

                        if (!(child as any).__runtime) {
                            (child as any).__runtime = {};
                        }
                        (child as any).__runtime.isInit = true;
                    }
                });

                return layer;
            });

            if (isTextMaterial) {
                const bgLayer = getDefaultClientBackgroundValue(stageWidth, stageHeight);
                layers.unshift(bgLayer);
            }

            templateConf.push({
                width: stageWidth,
                height: stageHeight,
                name,
                layers,
                settingType: null,
            });
        });

        // TODO PPT解析还没搬 加载解析后模版的所有字体文件
        // const fontURLMap = keyBy(fonts, 'font_name');

        // for (const fontFamily of fontsRequired) {
        //     // HACK: 如果字体不存在，则不加载
        //     if (fontURLMap[fontFamily]) {
        //         loadFontAndStoreAsync(fontFamily, fontURLMap[fontFamily].zip_url);
        //     }
        // }

        const tName = name;
        let canvas_type = stages.length > CanvasType.SINGLE ? CanvasType.VARIETY : CanvasType.SINGLE;

        const isPPT = name.startsWith('ppt_') || isFromPPTUpload;

        if (isPPT) {
            canvas_type = CanvasType.PPT;
        }
        setCanvasType(canvas_type);

        if (canvas_type === CanvasType.VARIETY) {
            editor.stage.setType(StageType.VARIETY, canvas_type);
        } else {
            editor.stage.setType(StageType.SINGLE, canvas_type);
        }
        editor.stage.setUnit(SizeUnitType.px);
        setTemplateName(tName || name || '未命名的设计');
        initTemplateInfo({
            id: '',
            preview,
            version,
            isArray: true,
            templateName: tName || name || '未命名的设计',
            canvasType: canvas_type,
            resolution: resolution,
            qrCode: '',
            psdFileUrl: '',
            useWatermark: true,
        });

        const extraInfos: IExtraInfo[] = [];
        templateConf.forEach((element: any) => {
            const { settingType, width, height } = element;
            extraInfos.push({ settingType, width, height });
        });

        // 添加画布的额外信息
        editor.stage.insertWithoutHistory(
            templateConf.map((item: any) => item.layers),
            extraInfos,
        );

        const effectAllLayers = [] as ILayerProps[];
        for (let i = 0; i < templateConf.length; i++) {
            traverseTrees(templateConf[i].layers, (layer: ILayerProps) => {
                if (layer.layerType === LayerType.IMAGE_LAYER && (layer.data as IImageLayerProps).effect) {
                    effectAllLayers.push(layer);
                }
            });
        }

        for (let i = 0; i < effectAllLayers.length; i++) {
            const url = await editor.imageLayer.runEffectAndGetUrl(effectAllLayers[i]);

            // (
            //     (effectAllLayers[i].data as IImageLayerProps).effect as any
            // ).screenshotUrl = url;
            ((effectAllLayers[i].data as IImageLayerProps).effect as any).screenshotUrl =
                await base64Kit.getRemoteUrlAsync(url || '');

            editor.imageLayer.updateLayer({ ...effectAllLayers[i] });
        }
    };

    const uploadPsdAndCreateTemplate = () => {
        const isTextMaterial = isUploadTextMaterialPsd();

        const fileMap = {} as any;
        const fonts = editor.font.getList();

        PsdParseModal.show({
            uploadFileToOBS: async (file: File, extension?: string) => {
                const md5 = await readFileMd5(file);

                if (fileMap[md5]) {
                    return fileMap[md5];
                }
                const result = await uploadFileToObs(file);

                fileMap[md5] = result.url;
                return result.url;
            },
            finishCallback: async (data: any | null) => {
                if (isUploadTextMaterialPsd() && data && data.psdFile) {
                    const { psdFile } = data;
                    const { url } = await uploadFileToObs(psdFile, undefined, 'psd');
                    setPsdFileUrl(url);
                }

                if (data.file) {
                    createTemplateByPsd(
                        {
                            psdChild: [],
                            psdFile: null as any,
                            name: data.name || '',
                            resolution: data.resolution,
                            stages: data.templateConf.map((item: any) => {
                                adjustFontFamily(item.layers, fonts);

                                return {
                                    clientLayers: item.layers,
                                    preview: item.preview || '',
                                    stageWidth: item.width,
                                    stageHeight: item.height,
                                    replaceFonts: [],
                                    name: item.name || '',
                                    settingType: item.settingType,
                                };
                            }),
                        },
                        false,
                        true,
                    );
                } else {
                    createTemplateByPsd(data, isTextMaterial, false);
                }
            },
            fonts: fonts.map((font) => {
                return {
                    fontName: font.font_name || font.default_name || '',
                    fontUrl: font.zip_url,
                };
            }),
            title: '文件上传解析',
            helpDocUrl: !isTextMaterial
                ? 'https://www.yuque.com/meitumcp/vvtts5/yrz632?from_wecom=1'
                : 'https://doc.weixin.qq.com/doc/w3_AYUA8Aa9AKgAaDUjyB0SjaFVns1Bt?scode=ACIAJAeGAAgxIz1iCtALwAYgZjADo',
            // parseNoLimit: true,
            // noReplaceFontFamily: true,

            noParseBgLayer: isTextMaterial,
        });
    };

    const uploadPPTAndCreateTemplate = () => {
        const fileMap = {} as any;
        const fonts = editor.font.getList();
        PsdParseModal.show({
            uploadFileToOBS: async (file: File, extension?: string) => {
                const md5 = await readFileMd5(file);

                if (fileMap[md5]) {
                    return fileMap[md5];
                }
                const result = await uploadFileToObs(file);

                fileMap[md5] = result.url;
                return result.url;
            },
            finishCallback: async (data: any | null) => {
                if (data.file) {
                    createTemplateByPsd(
                        {
                            psdChild: [],
                            psdFile: null as any,
                            name: data.name || '',
                            resolution: data.resolution,
                            stages: data.templateConf.map((item: any) => {
                                adjustFontFamily(item.layers, fonts);

                                return {
                                    clientLayers: item.layers,
                                    preview: item.preview || '',
                                    stageWidth: item.width,
                                    stageHeight: item.height,
                                    replaceFonts: [],
                                    name: item.name || '',
                                    settingType: item.settingType,
                                };
                            }),
                        },
                        false,
                        true,
                    );
                } else {
                    createTemplateByPsd(data, false, false);
                }
            },
            fonts: fonts.map((font) => {
                return {
                    fontName: font.font_name || font.default_name || '',
                    fontUrl: font.zip_url,
                };
            }),
            title: '文件上传解析',
            helpDocUrl: 'https://www.yuque.com/meitumcp/vvtts5/yrz632?from_wecom=1',
            // parseNoLimit: true,
            // noReplaceFontFamily: true,

            noParseBgLayer: false,
        });
    };

    const initStage = async (templateDetail: any, isEditTextTemplate = false) => {
        const data = templateDetail.data?.response || templateDetail.data?.data;
        const fonts = editor.font.getList();
        if (!data) {
            return;
        }

        const tId = data.id;
        const tName = data.default_name || data.name;
        if (!tId) {
            return;
        }
        const qr_code = data.qr_code;

        setQrCode(qr_code);

        setCanvasType(data.canvas_type);
        if (data.canvas_type === CanvasType.VARIETY) {
            editor.stage.setType(StageType.VARIETY, data.canvas_type);
            featureHelper.use(CollapseType.NAVIGATOR_MENU);
        } else {
            editor.stage.setType(StageType.SINGLE, data.canvas_type);
            featureHelper.use(CollapseType.LAYER_MENU);
        }

        const {
            extraInfo,
            template: { preview, version, name, templateConf, confIsArray, unit },
            replaceFamilies,
        } = await transferTemplateToClient(templateDetail, fonts, data.id);

        if (extraInfo) {
            setExtraInfo(extraInfo);
        }

        editor.stage.setUnit(unit);

        setTemplateName(tName || name || '未命名的设计');
        initTemplateInfo({
            id: data.id,
            preview,
            version,
            isArray: confIsArray,
            templateName: tName || name || '未命名的设计',
            canvasType: data.canvas_type,
            resolution: data.resolution,
            qrCode: qr_code,
            psdFileUrl: '',
            useWatermark: true,
        });
        // template.name = template.name.replace(/.psd/g, '');

        if (replaceFamilies.length > 0) {
            Message.info(`${replaceFamilies.join('、')}字体已下架，已帮您替换成阿里巴巴惠普字体`);
        }

        const extraInfos: IExtraInfo[] = [];
        templateConf.forEach((element: any) => {
            if (isEditTextTemplate || element.layers[0].layerType !== LayerType.BACKGROUND_LAYER) {
                const bgLayer = getDefaultClientBackgroundValue(element.width, element.height);
                element.layers.unshift(bgLayer);
            }

            const { settingType, width, height, extraInfo } = element;
            extraInfos.push({ settingType, width, height, extraInfo });
        });

        editor.stage.insertWithoutHistory(
            templateConf.map((item: any) => item.layers),
            extraInfos,
        );
        setServerTemplateId(data.material_id);
        setMCPSourceId(data.source_id);
    };

    const initTextTemplateStage = async (templateDetail: any) => {
        if (!templateDetail) {
            return;
        }

        const { config, id, preview, name, formula } = templateDetail;
        const tId = id;
        const tName = name;

        if (!tId) {
            return;
        }
        const fonts = editor.font.getList();
        setCanvasType(1);

        editor.stage.setType(StageType.SINGLE, 1);
        featureHelper.use(CollapseType.LAYER_MENU);
        const serverLayerData = JSON.parse(config || formula);
        const { templateConfs: layer, replaceFamilies } = await transferServerMaterialLayerToClient(
            serverLayerData,
            serverLayerData.width,
            serverLayerData.height,
            fonts,
        );
        if (replaceFamilies.length > 0) {
            Message.info(`${replaceFamilies.join('、')}字体已下架，已帮您替换成阿里巴巴惠普字体`);
        }
        setTemplateName(tName || name || '未命名的设计');

        initTemplateInfo({
            id: id,
            preview,
            version: serverLayerData.version,
            isArray: false,
            templateName: tName || name || '未命名的设计',
            canvasType: 1,
            resolution: 1,
            qrCode: '',
            psdFileUrl: '',
            useWatermark: true,
        });

        // TODO if (replaceFamilys.length > 0) {
        //     Message.info(
        //         `${replaceFamilys.join(
        //             '、'
        //         )}字体已下架，已帮您替换成阿里巴巴惠普字体`
        //     );
        // }

        const bgLayer = getDefaultClientBackgroundValue(serverLayerData.width, serverLayerData.height);
        const extraInfos: IExtraInfo[] = [];
        extraInfos.push({
            width: serverLayerData.width,
            height: serverLayerData.height,
        });

        editor.stage.insertWithoutHistory([[bgLayer, layer]], extraInfos);
    };

    const createEmptyTemplate = async (
        stageSize?: {
            stageWidth: number;
            stageHeight: number;
        },
        bgId?: string,
    ) => {
        const queryParams: any = queryString.parse(window.location.search);
        const { stageWidth = 1242, stageHeight = 2688 } = queryParams;
        editor.stage.setType(StageType.SINGLE, 1);
        featureHelper.use(CollapseType.LAYER_MENU);
        setTemplateName('未命名的设计');
        const defaultBg = getDefaultClientBackgroundValue(
            stageSize?.stageWidth || stageWidth,
            stageSize?.stageHeight || stageHeight,
        );

        defaultBg.id = bgId || uuid4();

        // 如果是编辑素材那背景默认为#e7e7e7
        if (isEditTagMaterial()) {
            (defaultBg.data as IBackgroundLayerProps).backgroundColor = '#e7e7e7ff';
        }

        editor.stage.insertWithoutHistory([[defaultBg]], []);
    };

    // 划重点初始化
    const initEditTextMark = async (id: string, uploadType: string) => {
        const { width: stageWidth, height: stageHeight } = TextMarkDefaultStage;
        await createEmptyTemplate({
            stageWidth,
            stageHeight,
        });

        const textLayer = await createTextLayer(
            {
                width: stageWidth,
                height: stageHeight,
            },
            { text: TextMarkDefaultText, color: '#000000ff', fontSize: 80, fontFamily: TextMarkDefaultFontFamily },
        );

        editor.layer.add(textLayer);

        if (id) {
            // 获取划重点资源
            const {
                data: {
                    data: { id: materialId, material_conf, animate, default_name, use_black_text },
                },
            } = await fetchTextMark(id);

            if (!material_conf) return;

            const materialConf = JSON.parse(material_conf);

            materialConf.defaultColor = materialConf.defaultColor
                ? rgbaToHexa(hexToRgba(materialConf.defaultColor))
                : '#ffffffff';

            const textMark = {
                materialId: `${materialId}`,
                color: materialConf.defaultColor,
                size: 50,
                enableSize: materialConf.enableSize,
            };

            const newMarkConfig = {
                animate: animate || 0,
                materialId: `${materialId}`,
                data: materialConf,
                useBlackText: use_black_text,
            };

            setTextLayerId(textLayer.id);
            setMarkConfig(newMarkConfig);
            await loadTextMarkSVG([newMarkConfig]);
            setTextMarkSvg(newMarkConfig);
            editor.textLayer.setTextMark(textLayer.id, textMark);
            default_name && setTemplateName(default_name);
        } else if (uploadType === 'svg') {
            // 上传 svg
            TextMarkSvgUpload.show('textMark', textLayer.id, async (markItem) => {
                setTextLayerId(textLayer.id);
                setMarkConfig(markItem);
                setTextMarkSvg(markItem);
                const textMark = {
                    materialId: markItem.materialId,
                    color: markItem.data.defaultColor,
                    size: 50,
                    enableSize: markItem.data.enableSize,
                };
                editor.textLayer.setTextMark(textLayer.id, textMark);
            });
        }
        editor.active.delete();
    };

    // 文字特效初始化
    const initEditTextArt = async (id: string, uploadType: string) => {
        const { width: stageWidth, height: stageHeight } = TextArtDefaultStage;
        await createEmptyTemplate({
            stageWidth,
            stageHeight,
        });

        const textLayer = await createTextLayer(
            {
                width: stageWidth,
                height: stageHeight,
            },
            { text: TextArtDefaultText, color: '#000000ff', fontSize: 80, fontFamily: TextArtDefaultFontFamily },
        );

        editor.stage.updateLayer(textLayer);

        if (id) {
            // 获取文字特效
            const {
                data: {
                    data: { config, default_name, use_black_text, thumbnail, thumbnail_black, default_text },
                },
            } = await fetchTextArt(id);

            const { width, height } = thumbnail ?? thumbnail_black ?? {};

            if (!config || !width || !height) return;

            const advancedTextMaterial = JSON.parse(config);

            const newTextLayer = cloneDeep(textLayer);

            (newTextLayer.data as ITextLayerProps).text = default_text || TextArtDefaultText;

            editor.stage.updateLayer(newTextLayer);

            setTextLayerId(textLayer.id);
            // 加载 SVG 资源
            await fetchSvgContentWithCache(advancedTextMaterial.material.src);
            setTextArtConfig({
                useBlackText: use_black_text,
            });
            editor.textLayer.applyArtText(textLayer.id, advancedTextMaterial, { writeHistory: false });
            default_name && setTemplateName(default_name);
        } else if (uploadType === 'svg') {
            // 上传 svg
            TextMarkSvgUpload.show('textArt', textLayer.id, async (data: IAdvancedTextMaterial) => {
                setTextLayerId(textLayer.id);
                setTextArtConfig({
                    useBlackText: 1,
                });
                editor.textLayer.applyArtText(textLayer.id, data);
            });
        }
        editor.active.delete();
    };

    const addMaterial = async (id: string, stageWidth: number, stageHeight: number) => {
        const queryParams: any = queryString.parse(window.location.search);
        const data = await getMaterialLayerInfoById(id, queryParams.material_id);
        const layerInfo = JSON.parse((data.data as any).data.layer_formula);
        const tagInfoId = data.data.data.id;
        setTagInfoId(tagInfoId);
        const fonts = editor.font.getList();
        if (layerInfo.layer.layerType === LayerType.BACKGROUND_LAYER) {
            const img = await loadImageAsync(layerInfo.layer.url);
            await createEmptyTemplate(
                {
                    stageWidth: img.width,
                    stageHeight: img.height,
                },
                layerInfo.layer.id,
            );
            const option = {
                url: layerInfo.layer.url,
                backgroundColor: '',
            };
            editor.backgroundLayer.addBackgroundUrl(option);
        } else {
            await createEmptyTemplate();
            const { templateConfs: layer, replaceFamilies } = await transferServerMaterialLayerToClient(
                layerInfo,
                stageWidth,
                stageHeight,
                fonts,
            );
            if (replaceFamilies.length > 0) {
                Message.info(`${replaceFamilies.join('、')}字体已下架，已帮您替换成阿里巴巴惠普字体`);
            }
            await editor.textLayer.addByMaterialLayer(layer);
        }
    };

    const addSvgMaterial = async (id: string) => {
        const data = await fetchSvgMaterialDetail(id);
        const info = data.data.data;
        const svgInfo = JSON.parse(info.svg.info);
        await createEmptyTemplate({
            stageWidth: svgInfo.naturalWidth + 200,
            stageHeight: svgInfo.naturalHeight + 200,
        });
        addVectorLayer.add({ id: 0, ...info.svg });
    };

    return async () => {
        const queryParams: any = queryString.parse(window.location.search);

        const { tag_id, stageWidth = 1242, stageHeight = 2688, id, material_id, type, uploadType } = queryParams;

        try {
            if (isPPTDemoAndShowFullTemplate()) {
                // 套版模板
                return;
            } else if (isEditMaterial()) {
                addSvgMaterial(material_id);
            } else if (isAITemplate()) {
                // ai模板编辑
                const templateDetail = await fetchAITemplate(id);
                await initStage(templateDetail);
            } else if (isEditorWatermark()) {
                const templateDetail = await getWatermarkTemplate({
                    id,
                });
                const config = JSON.parse((templateDetail.data as any).data.config);
                if (config.extraInfo) {
                    setExtraInfo(config.extraInfo);
                }

                initTextTemplateStage((templateDetail.data as any).data);
            } else if (isTextTemplate()) {
                // 花字素材模板编辑
                // 开启模板闪烁

                const templateDetail = await fetchTextTemplateDetail({
                    id,
                });
                initTextTemplateStage((templateDetail.data as any).data || (templateDetail.data as any).response);
            } else if (isEditTagMaterial()) {
                // 编辑打标素材
                addMaterial(tag_id, stageWidth, stageHeight);
            } else if (isEditAIPPTTemplate()) {
                const rsp = await fetchAIPPTTemplateDetail(id);
                const { name, formula } = rsp.data.data;

                await initStage({
                    data: {
                        response: {
                            id,
                            name,
                            canvas_type: CanvasType.PPT,
                            formula,
                        },
                    },
                });
            } else if (isEditAIPPTComponent()) {
                const rsp = await fetchAIPPTTemplateComponent(id);
                const { name, formula } = rsp.data.data;
                await initStage({
                    data: {
                        response: {
                            id,
                            name,
                            canvas_type: CanvasType.SINGLE,
                            formula,
                        },
                    },
                });
            } else if (isEditTextMark()) {
                initEditTextMark(id, uploadType);
            } else if (isEditTextArt()) {
                initEditTextArt(id, uploadType);
            } else if (id) {
                // 普通模板编辑
                // 开启模板闪烁
                // shineHelper.enableLayerShine();
                const templateDetail = await fetchTemplate(id, queryParams.from === 'ggpt' ? '1' : '0');
                await initStage(templateDetail);
            } else if (isUploadTemplatePsd() || isUploadTextMaterialPsd()) {
                // 上传模板
                uploadPsdAndCreateTemplate();
            } else if (isUploadPPT() || isUploadAIPPTTemplate() || isUploadAIPPTComponent()) {
                uploadPPTAndCreateTemplate();
            } else {
                await createEmpty.action();
            }
            setCurrentType(CollapseType.LAYER_MENU);
        } catch (error) {
            console.error('请求模板失败:', error);
            console.error($t((v) => v.hooks.loadFailed));
            return null;
        } finally {
            // 闪烁只在加载模板时执行一次
            // 关闭模板闪烁
            // shineHelper.disableLayerShine();
        }
    };
};

export default useFetchTemplate;
